import React, { useState } from 'react';
import {
    BlockDefinition,
    LayoutStyle,
    LAYOUT_STYLE_LABELS,
    NavLinkConfig,
    FooterColumnConfig,
    SocialLinkConfig,
    AccountMenuItemConfig,
} from '../../types/homepage';

export interface ContentItem {
    id: string;
    name?: string;
    title?: string;
    description?: string;
    thumbnail?: string;
    backgroundImage?: string;
    badge?: string | null;
    icon?: string;
    color?: string;
    videoCount?: number;
    duration?: string;
    views?: number;
    channelName?: string;
    videoCount2?: number;
}

export interface DropdownConfig {
    key: string;
    label: string;
    options: string[];
}

export interface BlockConfig {
    title: string;
    subtitle: string;
    layoutStyle: LayoutStyle;
    fullWidth: boolean;
    backgroundColor: string;
    textColor: string;
    htmlContent: string;
    backgroundImage: string;
    overlayOpacity: number;
    eyebrow: string;
    ctaLabel: string;
    ctaUrl: string;
    ctaSecondaryLabel: string;
    ctaSecondaryUrl: string;
    searchPlaceholder: string;
    dropdowns: DropdownConfig[];
    showDuration: boolean;
    showViewCount: boolean;
    showChannelName: boolean;
    showDescription: boolean;
    autoPlay: boolean;
    tileColor: string;
    contentSourceType: string;
    contentLimit: number;
    content: ContentItem[];
    ctaButtonLabel: string;
    ctaButtonUrl: string;
    titleColor: string;
    subtitleColor: string;
    ctaBgColor: string;
    ctaTextColor: string;
    ctaSecondaryBgColor: string;
    ctaSecondaryTextColor: string;
    logoText: string;
    logoImage: string;
    logoUrl: string;
    navLinks: NavLinkConfig[];
    topBarLinks: NavLinkConfig[];
    showSearchInHeader: boolean;
    topBarBgColor: string;
    topBarTextColor: string;
    copyrightText: string;
    footerColumns: FooterColumnConfig[];
    socialLinks: SocialLinkConfig[];
    socialTitle: string;
    linkColor: string;
    socialIconColor: string;
    showAccountMenu: boolean;
    accountAvatarType: 'initials' | 'image';
    accountAvatarImage: string;
    accountAvatarBgColor: string;
    accountAvatarTextColor: string;
    accountName: string;
    accountEmail: string;
    accountMenuItems: AccountMenuItemConfig[];
    showLogoutInMenu: boolean;
    logoutLabel: string;
    accountMenuBgColor: string;
    accountMenuTextColor: string;
}


export function defaultConfig(): BlockConfig {
    return {
        title: '',
        subtitle: '',
        layoutStyle: 'default' as LayoutStyle,
        fullWidth: true,
        backgroundColor: '#ffffff',
        textColor: '#000000',
        htmlContent: '',
        backgroundImage: '',
        overlayOpacity: 0.5,
        eyebrow: '',
        ctaLabel: '',
        ctaUrl: '',
        ctaSecondaryLabel: '',
        ctaSecondaryUrl: '',
        searchPlaceholder: 'Search...',
        dropdowns: [],
        showDuration: true,
        showViewCount: true,
        showChannelName: true,
        showDescription: true,
        autoPlay: false,
        tileColor: '#1a3a5c',
        contentSourceType: 'custom',
        contentLimit: 10,
        content: [],
        ctaButtonLabel: '',
        ctaButtonUrl: '',
        titleColor: '#000000',
        subtitleColor: '#000000',
        ctaBgColor: '#ffffff',
        ctaTextColor: '#00897b',
        ctaSecondaryBgColor: '#1f2937',
        ctaSecondaryTextColor: '#ffffff',
        logoText: '',
        logoImage: '',
        logoUrl: '/',
        navLinks: [],
        topBarLinks: [],
        showSearchInHeader: true,
        topBarBgColor: '#00897b',
        topBarTextColor: '#ffffff',
        copyrightText: `© ${new Date().getFullYear()} All rights reserved.`,
        footerColumns: [],
        socialLinks: [],
        socialTitle: 'Follow Us',
        linkColor: '#c9ced9',
        socialIconColor: '#3d4a63',

        showAccountMenu: false,
        accountAvatarType: 'initials',
        accountAvatarImage: '',
        accountAvatarBgColor: '#e5e7eb',
        accountAvatarTextColor: '#111827',
        accountName: 'User',
        accountEmail: 'user@example.com',
        accountMenuItems: [
            { key: 'profile', label: 'My Profile', icon: '🪪', url: '/profile' },
            { key: 'activity', label: 'Activity', icon: '📈', url: '/activity' },
            { key: 'courses', label: 'Courses', icon: '📘', url: '/courses' },
            { key: 'purchases', label: 'Purchases', icon: '🧾', url: '/purchases' },
            { key: 'subscriptions', label: 'Subscriptions', icon: '🖥️', url: '/subscriptions' },
        ],
        showLogoutInMenu: true,
        logoutLabel: 'Log out',
        accountMenuBgColor: '#ffffff',
        accountMenuTextColor: '#111827',
    };
}

export function configFromBlock(block: {
    title?: string | null;
    subtitle?: string | null;
    layoutStyle?: string;
    contentSource?: { type: string; limit?: number };
    content?: ContentItem[];
    settings?: Record<string, unknown>;
}): BlockConfig {
    const s = block.settings || {};
    const base = defaultConfig();
    return {
        ...base,
        title: block.title || '',
        subtitle: block.subtitle || '',
        layoutStyle: (block.layoutStyle as LayoutStyle) || 'default',
        contentSourceType: block.contentSource?.type || 'custom',
        contentLimit: block.contentSource?.limit ?? 10,
        content: (block.content as ContentItem[]) || [],
        // settings fields
        fullWidth: (s.fullWidth as boolean) ?? base.fullWidth,
        backgroundColor: (s.backgroundColor as string) || base.backgroundColor,
        textColor: (s.textColor as string) || base.textColor,
        backgroundImage: (s.backgroundImage as string) || '',
        overlayOpacity: (s.overlayOpacity as number) ?? 0.5,
        eyebrow: (s.eyebrow as string) || '',
        ctaLabel: (s.ctaLabel as string) || '',
        ctaUrl: (s.ctaUrl as string) || '',
        ctaSecondaryLabel: (s.ctaSecondaryLabel as string) || '',
        ctaSecondaryUrl: (s.ctaSecondaryUrl as string) || '',
        searchPlaceholder: (s.searchPlaceholder as string) || 'Search...',
        dropdowns: (s.dropdowns as DropdownConfig[]) || [],
        showDuration: (s.showDuration as boolean) ?? true,
        showViewCount: (s.showViewCount as boolean) ?? true,
        showChannelName: (s.showChannelName as boolean) ?? true,
        showDescription: (s.showDescription as boolean) ?? true,
        autoPlay: (s.autoPlay as boolean) ?? false,
        tileColor: (s.tileColor as string) || '#1a3a5c',
        htmlContent: (s.htmlContent as string) || '',
        ctaButtonLabel: (s.ctaButtonLabel as string) || '',
        ctaButtonUrl: (s.ctaButtonUrl as string) || '',
        titleColor: (s.titleColor as string) || base.titleColor,
        subtitleColor: (s.subtitleColor as string) || base.subtitleColor,
        ctaBgColor: (s.ctaBgColor as string) || base.ctaBgColor,
        ctaTextColor: (s.ctaTextColor as string) || base.ctaTextColor,
        ctaSecondaryBgColor: (s.ctaSecondaryBgColor as string) || base.ctaSecondaryBgColor,
        ctaSecondaryTextColor: (s.ctaSecondaryTextColor as string) || base.ctaSecondaryTextColor,
        logoText: (s.logoText as string) || '',
        logoImage: (s.logoImage as string) || '',
        logoUrl: (s.logoUrl as string) || '/',
        navLinks: (s.navLinks as NavLinkConfig[]) || [],
        topBarLinks: (s.topBarLinks as NavLinkConfig[]) || [],
        showSearchInHeader: (s.showSearchInHeader as boolean) ?? true,
        topBarBgColor: (s.topBarBgColor as string) || '#00897b',
        topBarTextColor: (s.topBarTextColor as string) || '#ffffff',
        copyrightText: (s.copyrightText as string) || `© ${new Date().getFullYear()} All rights reserved.`,
        footerColumns: (s.footerColumns as FooterColumnConfig[]) || [],
        socialLinks: (s.socialLinks as SocialLinkConfig[]) || [],
        socialTitle: (s.socialTitle as string) || 'Follow Us',
        linkColor: (s.linkColor as string) || '#c9ced9',
        socialIconColor: (s.socialIconColor as string) || '#3d4a63',

        showAccountMenu: (s.showAccountMenu as boolean) ?? base.showAccountMenu,
        accountAvatarType: (s.accountAvatarType as 'initials' | 'image') || base.accountAvatarType,
        accountAvatarImage: (s.accountAvatarImage as string) || '',
        accountAvatarBgColor: (s.accountAvatarBgColor as string) || base.accountAvatarBgColor,
        accountAvatarTextColor: (s.accountAvatarTextColor as string) || base.accountAvatarTextColor,
        accountName: (s.accountName as string) || base.accountName,
        accountEmail: (s.accountEmail as string) || base.accountEmail,
        accountMenuItems: (s.accountMenuItems as AccountMenuItemConfig[]) || base.accountMenuItems,
        showLogoutInMenu: (s.showLogoutInMenu as boolean) ?? base.showLogoutInMenu,
        logoutLabel: (s.logoutLabel as string) || base.logoutLabel,
        accountMenuBgColor: (s.accountMenuBgColor as string) || base.accountMenuBgColor,
        accountMenuTextColor: (s.accountMenuTextColor as string) || base.accountMenuTextColor,
    };
}

// SHARED PRIMITIVES


export const inputStyle: React.CSSProperties = {
    width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid #e5e7eb',
    fontSize: 13, color: '#111827', outline: 'none', boxSizing: 'border-box', background: 'white',
};

export const labelStyle: React.CSSProperties = {
    fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: '#9ca3af',
    textTransform: 'uppercase', margin: '0 0 6px', display: 'block',
};

export function Section({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div style={{ marginBottom: 18 }}>
            <p style={labelStyle}>{label}</p>
            {children}
        </div>
    );
}

export function Row({ children }: { children: React.ReactNode }) {
    return <div style={{ display: 'flex', gap: 10 }}>{children}</div>;
}

export function Col({ children, flex = 1 }: { children: React.ReactNode; flex?: number }) {
    return <div style={{ flex }}>{children}</div>;
}

export function SubLabel({ children }: { children: React.ReactNode }) {
    return <p style={{ fontSize: 11, color: '#6b7280', margin: '0 0 5px' }}>{children}</p>;
}

export function ToggleRow({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0' }}>
            <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>
            <button onClick={() => onChange(!value)}
                style={{ position: 'relative', display: 'inline-flex', height: 20, width: 36, alignItems: 'center', borderRadius: 999, border: 'none', background: value ? '#3b82f6' : '#d1d5db', cursor: 'pointer', transition: 'background .2s', flexShrink: 0 }}>
                <span style={{ display: 'inline-block', height: 14, width: 14, borderRadius: '50%', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,.2)', transform: value ? 'translateX(18px)' : 'translateX(3px)', transition: 'transform .2s' }} />
            </button>
        </div>
    );
}

export function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
    return (
        <div style={{ flex: 1 }}>
            <SubLabel>{label}</SubLabel>
            <div style={{ display: 'flex', gap: 6 }}>
                <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)}
                    style={{ width: 34, height: 34, borderRadius: 6, border: '1px solid #e5e7eb', cursor: 'pointer', padding: 2 }} />
                <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
                    style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12 }} />
            </div>
        </div>
    );
}

function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
}

export function ImageUploadButton({ onChange, small, onError }: {
    onChange: (dataUrl: string) => void;
    small?: boolean;
    onError?: (message: string) => void;
}) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [loading, setLoading] = useState(false);

    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            onError?.('Please choose an image file.');
            if (inputRef.current) inputRef.current.value = '';
            return;
        }
        setLoading(true);
        try {
            const dataUrl = await readFileAsDataUrl(file);
            onChange(dataUrl);
        } catch (err) {
            console.error('Failed to read image file', err);
            onError?.('Could not read that image. Please try again.');
        } finally {
            setLoading(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    return (
        <>
            <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
            <button type="button" onClick={() => inputRef.current?.click()} disabled={loading}
                title="Upload image from computer"
                style={{
                    padding: small ? '0 10px' : '0 12px',
                    height: small ? 30 : 34,
                    borderRadius: 7,
                    border: '1px solid #e5e7eb',
                    background: loading ? '#f3f4f6' : '#f9fafb',
                    color: '#374151',
                    fontSize: small ? 11 : 12,
                    fontWeight: 600,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                }}>
                {loading ? 'Uploading…' : '⬆ Upload'}
            </button>
        </>
    );
}

export function ImageUrlField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
    const [error, setError] = useState<string | null>(null);
    return (
        <div>
            <SubLabel>{label}</SubLabel>
            <div style={{ display: 'flex', gap: 6 }}>
                <input type="text" value={value} onChange={(e) => { setError(null); onChange(e.target.value); }}
                    placeholder="https://... or upload from your computer" style={{ ...inputStyle, flex: 1 }} />
                <ImageUploadButton onChange={(dataUrl) => { setError(null); onChange(dataUrl); }} onError={setError} />
            </div>
            {error && <p style={{ fontSize: 11, color: '#ef4444', margin: '4px 0 0' }}>{error}</p>}
            {value && (
                <div style={{ marginTop: 6, borderRadius: 6, overflow: 'hidden', height: 80, background: '#f3f4f6' }}>
                    <img src={value} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>
            )}
        </div>
    );
}

// HTML EDITOR

export function HtmlEditor({ value, onChange }: { value: string; onChange: (html: string) => void }) {
    const [preview, setPreview] = useState(false);
    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, letterSpacing: '0.05em' }}>RAW HTML / CSS / JS</span>
                <div style={{ display: 'flex', gap: 4 }}>
                    {([false, true] as const).map((isPreview) => (
                        <button key={String(isPreview)} onClick={() => setPreview(isPreview)}
                            style={{ padding: '3px 10px', borderRadius: 5, border: '1px solid #e5e7eb', background: preview === isPreview ? '#2563eb' : 'white', color: preview === isPreview ? 'white' : '#6b7280', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                            {isPreview ? '👁 Preview' : '</> Code'}
                        </button>
                    ))}
                </div>
            </div>
            {!preview ? (
                <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={10} spellCheck={false}
                    placeholder={'<div class="my-block">\n  <!-- Your HTML here -->\n</div>'}
                    style={{ width: '100%', padding: '10px 12px', border: 'none', outline: 'none', fontSize: 12, fontFamily: "'Fira Code', Consolas, monospace", lineHeight: 1.6, resize: 'vertical', minHeight: 180, boxSizing: 'border-box', background: '#fafafa', color: '#111827' }}
                    onKeyDown={(e) => {
                        if (e.key === 'Tab') {
                            e.preventDefault();
                            const el = e.currentTarget;
                            const s = el.selectionStart;
                            const newVal = el.value.substring(0, s) + '  ' + el.value.substring(el.selectionEnd);
                            onChange(newVal);
                            requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = s + 2; });
                        }
                    }}
                />
            ) : (
                <iframe title="Preview" sandbox="allow-scripts allow-same-origin" srcDoc={value}
                    style={{ width: '100%', height: 220, border: 'none', display: 'block', background: 'white' }} />
            )}
        </div>
    );
}

// CONTENT ITEMS EDITOR

type ContentItemField = { key: keyof ContentItem; label: string; placeholder?: string; type?: 'text' | 'number' | 'color' | 'image' };

export function ContentItemsEditor({ items, onChange, fields, addLabel }: {
    items: ContentItem[];
    onChange: (items: ContentItem[]) => void;
    fields: ContentItemField[];
    addLabel: string;
}) {
    const addItem = () => onChange([...items, { id: `item-${Date.now()}` }]);
    const removeItem = (idx: number) => onChange(items.filter((_, i) => i !== idx));
    const updateItem = (idx: number, key: keyof ContentItem, value: unknown) =>
        onChange(items.map((item, i) => i === idx ? { ...item, [key]: value } : item));

    return (
        <div>
            {items.map((item, idx) => (
                <div key={item.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 8, background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Item {idx + 1}</span>
                        <button onClick={() => removeItem(idx)}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13, padding: '2px 6px', borderRadius: 4 }}>
                            ✕ Remove
                        </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {fields.map((field) => (
                            <div key={field.key}>
                                <SubLabel>{field.label}</SubLabel>
                                {field.type === 'color' ? (
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        <input type="color" value={(item[field.key] as string) || '#000000'}
                                            onChange={(e) => updateItem(idx, field.key, e.target.value)}
                                            style={{ width: 34, height: 34, borderRadius: 6, border: '1px solid #e5e7eb', padding: 2, cursor: 'pointer' }} />
                                        <input type="text" value={(item[field.key] as string) || ''}
                                            onChange={(e) => updateItem(idx, field.key, e.target.value)}
                                            placeholder={field.placeholder} style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12 }} />
                                    </div>
                                ) : field.type === 'image' ? (
                                    <div>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <input type="text" value={(item[field.key] as string) || ''}
                                                onChange={(e) => updateItem(idx, field.key, e.target.value)}
                                                placeholder={field.placeholder || 'https://... or upload'} style={{ ...inputStyle, flex: 1 }} />
                                            <ImageUploadButton small onChange={(dataUrl) => updateItem(idx, field.key, dataUrl)} />
                                        </div>
                                        {item[field.key] && (
                                            <div style={{ marginTop: 4, borderRadius: 5, overflow: 'hidden', height: 56, background: '#e5e7eb' }}>
                                                <img src={item[field.key] as string} alt=""
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <input type={field.type || 'text'}
                                        value={(item[field.key] as string | number) || ''}
                                        onChange={(e) => updateItem(idx, field.key, field.type === 'number' ? Number(e.target.value) : e.target.value)}
                                        placeholder={field.placeholder} style={inputStyle} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            <button onClick={addItem}
                style={{ width: '100%', padding: '8px', borderRadius: 7, border: '1.5px dashed #d1d5db', background: 'white', color: '#6b7280', fontSize: 13, cursor: 'pointer', marginTop: 2 }}>
                + {addLabel}
            </button>
        </div>
    );
}

// DROPDOWNS EDITOR

export function DropdownsEditor({ dropdowns, onChange }: { dropdowns: DropdownConfig[]; onChange: (d: DropdownConfig[]) => void }) {
    const add = () => onChange([...dropdowns, { key: `filter_${Date.now()}`, label: '', options: [] }]);
    const remove = (i: number) => onChange(dropdowns.filter((_, idx) => idx !== i));
    const update = (i: number, field: keyof DropdownConfig, value: string | string[]) =>
        onChange(dropdowns.map((d, idx) => idx === i ? { ...d, [field]: value } : d));
    const updateOptions = (i: number, raw: string) =>
        update(i, 'options', raw.split('\n').map((s) => s.trim()).filter(Boolean));

    return (
        <div>
            {dropdowns.map((dd, i) => (
                <div key={dd.key} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 8, background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Filter {i + 1}</span>
                        <button onClick={() => remove(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13, padding: '2px 6px', borderRadius: 4 }}>✕</button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                        <div>
                            <SubLabel>Key (no spaces)</SubLabel>
                            <input type="text" value={dd.key} onChange={(e) => update(i, 'key', e.target.value)} placeholder="e.g. content_type" style={inputStyle} />
                        </div>
                        <div>
                            <SubLabel>Label</SubLabel>
                            <input type="text" value={dd.label} onChange={(e) => update(i, 'label', e.target.value)} placeholder="e.g. Content Type" style={inputStyle} />
                        </div>
                        <div>
                            <SubLabel>Options (one per line)</SubLabel>
                            <textarea value={dd.options.join('\n')} onChange={(e) => updateOptions(i, e.target.value)} rows={4}
                                placeholder={"All\nVideos\nDocuments"} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
                        </div>
                    </div>
                </div>
            ))}
            <button onClick={add} style={{ width: '100%', padding: '8px', borderRadius: 7, border: '1.5px dashed #d1d5db', background: 'white', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>
                + Add filter dropdown
            </button>
        </div>
    );
}

export function NavLinksEditor({ links, onChange, addLabel }: {
    links: NavLinkConfig[];
    onChange: (l: NavLinkConfig[]) => void;
    addLabel: string;
}) {
    const add = () => onChange([...links, { key: `link_${Date.now()}`, label: '', url: '' }]);
    const remove = (i: number) => onChange(links.filter((_, idx) => idx !== i));
    const update = (i: number, field: keyof NavLinkConfig, value: string) =>
        onChange(links.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));

    return (
        <div>
            {links.map((link, i) => (
                <div key={link.key} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 8, background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Link {i + 1}</span>
                        <button onClick={() => remove(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13, padding: '2px 6px', borderRadius: 4 }}>✕</button>
                    </div>
                    <Row>
                        <Col>
                            <SubLabel>Label</SubLabel>
                            <input type="text" value={link.label} onChange={(e) => update(i, 'label', e.target.value)} placeholder="e.g. Categories" style={inputStyle} />
                        </Col>
                        <Col>
                            <SubLabel>URL</SubLabel>
                            <input type="text" value={link.url} onChange={(e) => update(i, 'url', e.target.value)} placeholder="/categories" style={inputStyle} />
                        </Col>
                    </Row>
                </div>
            ))}
            <button onClick={add} style={{ width: '100%', padding: '8px', borderRadius: 7, border: '1.5px dashed #d1d5db', background: 'white', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>
                + {addLabel}
            </button>
        </div>
    );
}
// NAV LINKS TREE EDITOR (with nested submenus)

export function NavLinksTreeEditor({ links, onChange, addLabel, depth = 0 }: {
    links: NavLinkConfig[];
    onChange: (l: NavLinkConfig[]) => void;
    addLabel: string;
    depth?: number;
}) {
    const add = () =>
        onChange([...links, { key: `link_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, label: '', url: '' }]);
    const remove = (i: number) => onChange(links.filter((_, idx) => idx !== i));
    const update = (i: number, field: 'label' | 'url', value: string) =>
        onChange(links.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
    const updateChildren = (i: number, children: NavLinkConfig[]) =>
        onChange(links.map((l, idx) => (idx === i ? { ...l, children } : l)));
    const addSubmenu = (i: number) =>
        onChange(links.map((l, idx) => (idx === i
            ? { ...l, children: [...(l.children || []), { key: `sub_${Date.now()}`, label: '', url: '' }] }
            : l)));

    const maxNestDepth = 2;

    return (
        <div>
            {links.map((link, i) => (
                <div key={link.key} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 8, background: depth === 0 ? '#fafafa' : '#f3f4f6' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>
                            {depth === 0 ? `Link ${i + 1}` : `Submenu item ${i + 1}`}
                        </span>
                        <button onClick={() => remove(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13, padding: '2px 6px', borderRadius: 4 }}>✕</button>
                    </div>
                    <Row>
                        <Col>
                            <SubLabel>Label</SubLabel>
                            <input type="text" value={link.label} onChange={(e) => update(i, 'label', e.target.value)} placeholder="e.g. Categories" style={inputStyle} />
                        </Col>
                        <Col>
                            <SubLabel>URL</SubLabel>
                            <input type="text" value={link.url} onChange={(e) => update(i, 'url', e.target.value)} placeholder="/categories" style={inputStyle} />
                        </Col>
                    </Row>

                    {depth < maxNestDepth && (
                        <div style={{ marginTop: 10, paddingLeft: 12, borderLeft: '2px solid #e5e7eb' }}>
                            <SubLabel>Submenu items {link.children?.length ? `(${link.children.length})` : ''}</SubLabel>
                            {link.children && link.children.length > 0 ? (
                                <NavLinksTreeEditor
                                    links={link.children}
                                    onChange={(c) => updateChildren(i, c)}
                                    addLabel="Add submenu item"
                                    depth={depth + 1}
                                />
                            ) : (
                                <button onClick={() => addSubmenu(i)}
                                    style={{ width: '100%', padding: '6px', borderRadius: 6, border: '1.5px dashed #d1d5db', background: 'white', color: '#6b7280', fontSize: 12, cursor: 'pointer' }}>
                                    + Add submenu item
                                </button>
                            )}
                        </div>
                    )}
                </div>
            ))}
            <button onClick={add} style={{ width: '100%', padding: '8px', borderRadius: 7, border: '1.5px dashed #d1d5db', background: 'white', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>
                + {addLabel}
            </button>
        </div>
    );
}

export function FooterColumnsEditor({ columns, onChange }: {
    columns: FooterColumnConfig[];
    onChange: (c: FooterColumnConfig[]) => void;
}) {
    const add = () => onChange([...columns, { key: `col_${Date.now()}`, title: '', links: [] }]);
    const remove = (i: number) => onChange(columns.filter((_, idx) => idx !== i));
    const updateTitle = (i: number, title: string) =>
        onChange(columns.map((c, idx) => (idx === i ? { ...c, title } : c)));
    const updateLinks = (i: number, links: NavLinkConfig[]) =>
        onChange(columns.map((c, idx) => (idx === i ? { ...c, links } : c)));

    return (
        <div>
            {columns.map((col, i) => (
                <div key={col.key} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 10, background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Column {i + 1}</span>
                        <button onClick={() => remove(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13, padding: '2px 6px', borderRadius: 4 }}>✕ Remove</button>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <SubLabel>Column title</SubLabel>
                        <input type="text" value={col.title} onChange={(e) => updateTitle(i, e.target.value)} placeholder="e.g. Quick Links" style={inputStyle} />
                    </div>
                    <SubLabel>Links</SubLabel>
                    <NavLinksEditor links={col.links} onChange={(l) => updateLinks(i, l)} addLabel="Add link" />
                </div>
            ))}
            <button onClick={add} style={{ width: '100%', padding: '8px', borderRadius: 7, border: '1.5px dashed #d1d5db', background: 'white', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>
                + Add column
            </button>
        </div>
    );
}

export function SocialLinksEditor({ links, onChange }: {
    links: SocialLinkConfig[];
    onChange: (l: SocialLinkConfig[]) => void;
}) {
    const add = () => onChange([...links, { key: `social_${Date.now()}`, label: '', url: '' }]);
    const remove = (i: number) => onChange(links.filter((_, idx) => idx !== i));
    const update = (i: number, field: keyof SocialLinkConfig, value: string) =>
        onChange(links.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));

    return (
        <div>
            {links.map((link, i) => (
                <div key={link.key} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 8, background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Social {i + 1}</span>
                        <button onClick={() => remove(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13, padding: '2px 6px', borderRadius: 4 }}>✕</button>
                    </div>
                    <Row>
                        <Col flex={0.6}>
                            <SubLabel>Letter / initial</SubLabel>
                            <input type="text" maxLength={2} value={link.label} onChange={(e) => update(i, 'label', e.target.value)} placeholder="F" style={inputStyle} />
                        </Col>
                        <Col flex={1.4}>
                            <SubLabel>URL</SubLabel>
                            <input type="text" value={link.url} onChange={(e) => update(i, 'url', e.target.value)} placeholder="https://facebook.com/..." style={inputStyle} />
                        </Col>
                    </Row>
                </div>
            ))}
            <button onClick={add} style={{ width: '100%', padding: '8px', borderRadius: 7, border: '1.5px dashed #d1d5db', background: 'white', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>
                + Add social link
            </button>
        </div>
    );
}

// ACCOUNT MENU ITEMS EDITOR

export function AccountMenuItemsEditor({ items, onChange }: {
    items: AccountMenuItemConfig[];
    onChange: (items: AccountMenuItemConfig[]) => void;
}) {
    const add = () => onChange([...items, { key: `menu_${Date.now()}`, label: '', url: '', icon: '•' }]);
    const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
    const update = (i: number, field: keyof AccountMenuItemConfig, value: string) =>
        onChange(items.map((item, idx) => (idx === i ? { ...item, [field]: value } : item)));
    const move = (i: number, dir: -1 | 1) => {
        const target = i + dir;
        if (target < 0 || target >= items.length) return;
        const copy = [...items];
        [copy[i], copy[target]] = [copy[target], copy[i]];
        onChange(copy);
    };

    return (
        <div>
            {items.map((item, i) => (
                <div key={item.key} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 8, background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Item {i + 1}</span>
                        <div style={{ display: 'flex', gap: 2 }}>
                            <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up"
                                style={{ background: 'none', border: 'none', color: i === 0 ? '#d1d5db' : '#6b7280', cursor: i === 0 ? 'default' : 'pointer', fontSize: 13, padding: '2px 6px', borderRadius: 4 }}>
                                ↑
                            </button>
                            <button onClick={() => move(i, 1)} disabled={i === items.length - 1} title="Move down"
                                style={{ background: 'none', border: 'none', color: i === items.length - 1 ? '#d1d5db' : '#6b7280', cursor: i === items.length - 1 ? 'default' : 'pointer', fontSize: 13, padding: '2px 6px', borderRadius: 4 }}>
                                ↓
                            </button>
                            <button onClick={() => remove(i)}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13, padding: '2px 6px', borderRadius: 4 }}>
                                ✕
                            </button>
                        </div>
                    </div>
                    <Row>
                        <Col flex={0.5}>
                            <SubLabel>Icon (emoji)</SubLabel>
                            <input type="text" value={item.icon} onChange={(e) => update(i, 'icon', e.target.value)} placeholder="🪪" style={inputStyle} />
                        </Col>
                        <Col flex={1.2}>
                            <SubLabel>Label</SubLabel>
                            <input type="text" value={item.label} onChange={(e) => update(i, 'label', e.target.value)} placeholder="e.g. My Profile" style={inputStyle} />
                        </Col>
                    </Row>
                    <div style={{ marginTop: 7 }}>
                        <SubLabel>URL</SubLabel>
                        <input type="text" value={item.url} onChange={(e) => update(i, 'url', e.target.value)} placeholder="/profile" style={inputStyle} />
                    </div>
                </div>
            ))}
            <button onClick={add} style={{ width: '100%', padding: '8px', borderRadius: 7, border: '1.5px dashed #d1d5db', background: 'white', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>
                + Add menu item
            </button>
        </div>
    );
}

// TYPE-SPECIFIC FIELD PANELS

function WelcomeBannerFields({ config, onChange }: { config: BlockConfig; onChange: (p: Partial<BlockConfig>) => void }) {
    return (
        <>
            <Section label="CONTENT">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div><SubLabel>Eyebrow text</SubLabel>
                        <input type="text" value={config.eyebrow} onChange={(e) => onChange({ eyebrow: e.target.value })} placeholder="e.g. Content Hub" style={inputStyle} /></div>
                    <div><SubLabel>Title</SubLabel>
                        <input type="text" value={config.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="Banner headline" style={inputStyle} /></div>
                    <div><SubLabel>Subtitle / body text</SubLabel>
                        <textarea value={config.subtitle} onChange={(e) => onChange({ subtitle: e.target.value })} rows={3} placeholder="Descriptive text…" style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} /></div>
                </div>
            </Section>
            <Section label="TEXT COLORS">
                <Row>
                    <ColorField label="Title color" value={config.titleColor} onChange={(v) => onChange({ titleColor: v })} />
                    <ColorField label="Subtitle color" value={config.subtitleColor} onChange={(v) => onChange({ subtitleColor: v })} />
                </Row>
            </Section>
            <Section label="BACKGROUND">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <ImageUrlField label="Background image" value={config.backgroundImage} onChange={(v) => onChange({ backgroundImage: v })} />
                    <div>
                        <SubLabel>Overlay opacity: {Math.round(config.overlayOpacity * 100)}% (only applies when a background image is set)</SubLabel>
                        <input type="range" min={0} max={1} step={0.05} value={config.overlayOpacity}
                            onChange={(e) => onChange({ overlayOpacity: Number(e.target.value) })} style={{ width: '100%' }} />
                    </div>
                    <Row>
                        <ColorField label="Background color" value={config.backgroundColor} onChange={(v) => onChange({ backgroundColor: v })} />
                        <ColorField label="Base text color" value={config.textColor} onChange={(v) => onChange({ textColor: v })} />
                    </Row>
                </div>
            </Section>
            <Section label="PRIMARY CTA">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Row>
                        <Col><SubLabel>Button label</SubLabel>
                            <input type="text" value={config.ctaLabel} onChange={(e) => onChange({ ctaLabel: e.target.value })} placeholder="e.g. Explore" style={inputStyle} /></Col>
                        <Col><SubLabel>URL</SubLabel>
                            <input type="text" value={config.ctaUrl} onChange={(e) => onChange({ ctaUrl: e.target.value })} placeholder="/resources" style={inputStyle} /></Col>
                    </Row>
                    <Row>
                        <ColorField label="Button background" value={config.ctaBgColor} onChange={(v) => onChange({ ctaBgColor: v })} />
                        <ColorField label="Button text color" value={config.ctaTextColor} onChange={(v) => onChange({ ctaTextColor: v })} />
                    </Row>
                </div>
            </Section>
            <Section label="SECONDARY CTA">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Row>
                        <Col><SubLabel>Button label</SubLabel>
                            <input type="text" value={config.ctaSecondaryLabel} onChange={(e) => onChange({ ctaSecondaryLabel: e.target.value })} placeholder="e.g. Learn More" style={inputStyle} /></Col>
                        <Col><SubLabel>URL</SubLabel>
                            <input type="text" value={config.ctaSecondaryUrl} onChange={(e) => onChange({ ctaSecondaryUrl: e.target.value })} placeholder="/about" style={inputStyle} /></Col>
                    </Row>
                    <Row>
                        <ColorField label="Button background" value={config.ctaSecondaryBgColor} onChange={(v) => onChange({ ctaSecondaryBgColor: v })} />
                        <ColorField label="Button text color" value={config.ctaSecondaryTextColor} onChange={(v) => onChange({ ctaSecondaryTextColor: v })} />
                    </Row>
                </div>
            </Section>
        </>
    );
}

function HeaderFields({ config, onChange }: { config: BlockConfig; onChange: (p: Partial<BlockConfig>) => void }) {
    return (
        <>
            <Section label="LOGO">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div><SubLabel>Logo text (used if no image)</SubLabel>
                        <input type="text" value={config.logoText} onChange={(e) => onChange({ logoText: e.target.value })} placeholder="e.g. webcastcloud" style={inputStyle} /></div>
                    <ImageUrlField label="Logo image" value={config.logoImage} onChange={(v) => onChange({ logoImage: v })} />
                    <div><SubLabel>Logo link URL</SubLabel>
                        <input type="text" value={config.logoUrl} onChange={(e) => onChange({ logoUrl: e.target.value })} placeholder="/" style={inputStyle} /></div>
                </div>
            </Section>

            <Section label="TOP UTILITY BAR (optional)">
                <p style={{ fontSize: 11, color: '#9ca3af', margin: '0 0 8px' }}>Thin bar above the main header — e.g. links to related sites.</p>
                <Row>
                    <ColorField label="Bar background" value={config.topBarBgColor} onChange={(v) => onChange({ topBarBgColor: v })} />
                    <ColorField label="Bar text color" value={config.topBarTextColor} onChange={(v) => onChange({ topBarTextColor: v })} />
                </Row>
                <div style={{ height: 8 }} />
                <NavLinksEditor links={config.topBarLinks} onChange={(l) => onChange({ topBarLinks: l })} addLabel="Add top bar link" />
            </Section>

            <Section label="NAVIGATION MENU">
                <p style={{ fontSize: 11, color: '#9ca3af', margin: '0 0 8px' }}>{`Add a link, then use "+ Add submenu item" inside it to give that link a dropdown. Submenu items can have one further nested level of their own (e.g. Categories → Parent Category → Child Category).`}</p>
                <NavLinksTreeEditor links={config.navLinks} onChange={(l) => onChange({ navLinks: l })} addLabel="Add nav link" />
            </Section>

            <Section label="SEARCH">
                <ToggleRow label="Show search bar" value={config.showSearchInHeader} onChange={(v) => onChange({ showSearchInHeader: v })} />
                {config.showSearchInHeader && (
                    <div style={{ marginTop: 8 }}>
                        <SubLabel>Search placeholder</SubLabel>
                        <input type="text" value={config.searchPlaceholder} onChange={(e) => onChange({ searchPlaceholder: e.target.value })} placeholder="Search..." style={inputStyle} />
                    </div>
                )}
            </Section>

            <Section label="PRIMARY BUTTON (e.g. Register)">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Row>
                        <Col><SubLabel>Label</SubLabel>
                            <input type="text" value={config.ctaLabel} onChange={(e) => onChange({ ctaLabel: e.target.value })} placeholder="Register" style={inputStyle} /></Col>
                        <Col><SubLabel>URL</SubLabel>
                            <input type="text" value={config.ctaUrl} onChange={(e) => onChange({ ctaUrl: e.target.value })} placeholder="/register" style={inputStyle} /></Col>
                    </Row>
                    <Row>
                        <ColorField label="Button background" value={config.ctaBgColor} onChange={(v) => onChange({ ctaBgColor: v })} />
                        <ColorField label="Button text color" value={config.ctaTextColor} onChange={(v) => onChange({ ctaTextColor: v })} />
                    </Row>
                </div>
            </Section>

            <Section label="SECONDARY BUTTON (e.g. Local Login)">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Row>
                        <Col><SubLabel>Label</SubLabel>
                            <input type="text" value={config.ctaSecondaryLabel} onChange={(e) => onChange({ ctaSecondaryLabel: e.target.value })} placeholder="Local Login" style={inputStyle} /></Col>
                        <Col><SubLabel>URL</SubLabel>
                            <input type="text" value={config.ctaSecondaryUrl} onChange={(e) => onChange({ ctaSecondaryUrl: e.target.value })} placeholder="/login" style={inputStyle} /></Col>
                    </Row>
                    <Row>
                        <ColorField label="Button background" value={config.ctaSecondaryBgColor} onChange={(v) => onChange({ ctaSecondaryBgColor: v })} />
                        <ColorField label="Button text color" value={config.ctaSecondaryTextColor} onChange={(v) => onChange({ ctaSecondaryTextColor: v })} />
                    </Row>
                </div>
            </Section>

            <Section label="ACCOUNT MENU (logged-in user dropdown)">
                <ToggleRow label="Show account menu" value={config.showAccountMenu} onChange={(v) => onChange({ showAccountMenu: v })} />
                {config.showAccountMenu && (
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div>
                            <SubLabel>Avatar style</SubLabel>
                            <div style={{ display: 'flex', gap: 8 }}>
                                {(['initials', 'image'] as const).map((opt) => {
                                    const active = config.accountAvatarType === opt;
                                    return (
                                        <button key={opt} onClick={() => onChange({ accountAvatarType: opt })}
                                            style={{ flex: 1, padding: '7px 8px', borderRadius: 7, border: `1.5px solid ${active ? '#3b82f6' : '#e5e7eb'}`, background: active ? '#eff6ff' : 'white', color: active ? '#1d4ed8' : '#6b7280', fontSize: 12.5, fontWeight: 500, cursor: 'pointer' }}>
                                            {opt === 'initials' ? 'Initials' : 'Uploaded image'}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {config.accountAvatarType === 'image' ? (
                            <ImageUrlField label="Avatar image" value={config.accountAvatarImage} onChange={(v) => onChange({ accountAvatarImage: v })} />
                        ) : (
                            <Row>
                                <ColorField label="Avatar background" value={config.accountAvatarBgColor} onChange={(v) => onChange({ accountAvatarBgColor: v })} />
                                <ColorField label="Avatar initials color" value={config.accountAvatarTextColor} onChange={(v) => onChange({ accountAvatarTextColor: v })} />
                            </Row>
                        )}

                        <div>
                            <SubLabel>Placeholder user (demo data — swap for real auth data later)</SubLabel>
                            <Row>
                                <Col>
                                    <input type="text" value={config.accountName} onChange={(e) => onChange({ accountName: e.target.value })} placeholder="Full name" style={inputStyle} />
                                </Col>
                                <Col>
                                    <input type="text" value={config.accountEmail} onChange={(e) => onChange({ accountEmail: e.target.value })} placeholder="Email" style={inputStyle} />
                                </Col>
                            </Row>
                        </div>

                        <div>
                            <SubLabel>Menu items</SubLabel>
                            <AccountMenuItemsEditor items={config.accountMenuItems} onChange={(items) => onChange({ accountMenuItems: items })} />
                        </div>

                        <ToggleRow label="Show log out row" value={config.showLogoutInMenu} onChange={(v) => onChange({ showLogoutInMenu: v })} />
                        {config.showLogoutInMenu && (
                            <div>
                                <SubLabel>Log out label</SubLabel>
                                <input type="text" value={config.logoutLabel} onChange={(e) => onChange({ logoutLabel: e.target.value })} placeholder="Log out" style={inputStyle} />
                            </div>
                        )}

                        <Row>
                            <ColorField label="Dropdown background" value={config.accountMenuBgColor} onChange={(v) => onChange({ accountMenuBgColor: v })} />
                            <ColorField label="Dropdown text color" value={config.accountMenuTextColor} onChange={(v) => onChange({ accountMenuTextColor: v })} />
                        </Row>
                    </div>
                )}
            </Section>

            <Section label="HEADER BACKGROUND">
                <Row>
                    <ColorField label="Background color" value={config.backgroundColor} onChange={(v) => onChange({ backgroundColor: v })} />
                    <ColorField label="Text / nav link color" value={config.textColor} onChange={(v) => onChange({ textColor: v })} />
                </Row>
            </Section>
        </>
    );
}
function FooterFields({ config, onChange }: { config: BlockConfig; onChange: (p: Partial<BlockConfig>) => void }) {
    return (
        <>
            <Section label="LOGO & COPYRIGHT">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div><SubLabel>Logo text (used if no image)</SubLabel>
                        <input type="text" value={config.logoText} onChange={(e) => onChange({ logoText: e.target.value })} placeholder="e.g. webcastcloud" style={inputStyle} /></div>
                    <ImageUrlField label="Logo image" value={config.logoImage} onChange={(v) => onChange({ logoImage: v })} />
                    <div><SubLabel>Logo link URL</SubLabel>
                        <input type="text" value={config.logoUrl} onChange={(e) => onChange({ logoUrl: e.target.value })} placeholder="/" style={inputStyle} /></div>
                    <div><SubLabel>Copyright text</SubLabel>
                        <input type="text" value={config.copyrightText} onChange={(e) => onChange({ copyrightText: e.target.value })} placeholder="© 2025 Company. All rights reserved." style={inputStyle} /></div>
                </div>
            </Section>

            <Section label="LINK COLUMNS">
                <FooterColumnsEditor columns={config.footerColumns} onChange={(c) => onChange({ footerColumns: c })} />
            </Section>

            <Section label="SOCIAL LINKS">
                <div style={{ marginBottom: 8 }}>
                    <SubLabel>Section title</SubLabel>
                    <input type="text" value={config.socialTitle} onChange={(e) => onChange({ socialTitle: e.target.value })} placeholder="Follow Us" style={inputStyle} />
                </div>
                <SocialLinksEditor links={config.socialLinks} onChange={(l) => onChange({ socialLinks: l })} />
            </Section>

            <Section label="APPEARANCE">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Row>
                        <ColorField label="Background color" value={config.backgroundColor} onChange={(v) => onChange({ backgroundColor: v })} />
                        <ColorField label="Heading / text color" value={config.textColor} onChange={(v) => onChange({ textColor: v })} />
                    </Row>
                    <Row>
                        <ColorField label="Link color" value={config.linkColor} onChange={(v) => onChange({ linkColor: v })} />
                        <ColorField label="Social icon background" value={config.socialIconColor} onChange={(v) => onChange({ socialIconColor: v })} />
                    </Row>
                </div>
            </Section>
        </>
    );
}
function SearchFilterFields({ config, onChange }: { config: BlockConfig; onChange: (p: Partial<BlockConfig>) => void }) {
    return (
        <>
            <Section label="CONTENT">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div><SubLabel>Block title</SubLabel>
                        <input type="text" value={config.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="e.g. Filter Resources" style={inputStyle} /></div>
                    <div><SubLabel>Search placeholder</SubLabel>
                        <input type="text" value={config.searchPlaceholder} onChange={(e) => onChange({ searchPlaceholder: e.target.value })} placeholder="Search..." style={inputStyle} /></div>
                </div>
            </Section>
            <Section label="FILTER DROPDOWNS">
                <DropdownsEditor dropdowns={config.dropdowns} onChange={(d) => onChange({ dropdowns: d })} />
            </Section>
        </>
    );
}

function QuickLinksFields({ config, onChange }: { config: BlockConfig; onChange: (p: Partial<BlockConfig>) => void }) {
    const fields: ContentItemField[] = [
        { key: 'name', label: 'Name', placeholder: 'Link name' },
        { key: 'thumbnail', label: 'Thumbnail', type: 'image', placeholder: 'https://...' },
        { key: 'backgroundImage', label: 'Background Image', type: 'image', placeholder: 'https://...' },
        { key: 'badge', label: 'Badge text (optional)', placeholder: 'e.g. FEATURED' },
    ];
    return (
        <>
            <Section label="CONTENT">
                <div style={{ marginBottom: 8 }}>
                    <SubLabel>Block title</SubLabel>
                    <input type="text" value={config.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="e.g. Quick Links" style={inputStyle} />
                </div>
            </Section>
            <Section label="LINK ITEMS">
                <ContentItemsEditor items={config.content} onChange={(c) => onChange({ content: c })} fields={fields} addLabel="Add link item" />
            </Section>
        </>
    );
}

function CategoriesFields({ config, onChange, isTiled }: { config: BlockConfig; onChange: (p: Partial<BlockConfig>) => void; isTiled?: boolean }) {
    const fields: ContentItemField[] = [
        { key: 'title', label: 'Category name', placeholder: 'e.g. Getting Started' },
        { key: 'icon', label: 'Icon (emoji)', placeholder: '📌' },
        { key: 'color', label: 'Color', type: 'color' },
        { key: 'videoCount', label: 'Item count', type: 'number', placeholder: '0' },
    ];
    return (
        <>
            <Section label="CONTENT">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div><SubLabel>Block title</SubLabel>
                        <input type="text" value={config.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="e.g. Browse Categories" style={inputStyle} /></div>
                    {isTiled && (
                        <div>
                            <SubLabel>Tile background color</SubLabel>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <input type="color" value={config.tileColor} onChange={(e) => onChange({ tileColor: e.target.value })}
                                    style={{ width: 34, height: 34, borderRadius: 6, border: '1px solid #e5e7eb', padding: 2, cursor: 'pointer' }} />
                                <input type="text" value={config.tileColor} onChange={(e) => onChange({ tileColor: e.target.value })}
                                    style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12 }} />
                            </div>
                        </div>
                    )}
                </div>
            </Section>
            <Section label="CATEGORY ITEMS">
                <ContentItemsEditor items={config.content} onChange={(c) => onChange({ content: c })} fields={fields} addLabel="Add category" />
            </Section>
        </>
    );
}

function CarouselFields({ config, onChange }: { config: BlockConfig; onChange: (p: Partial<BlockConfig>) => void }) {
    const fields: ContentItemField[] = [
        { key: 'title', label: 'Title', placeholder: 'Video / content title' },
        { key: 'thumbnail', label: 'Thumbnail', type: 'image' },
        { key: 'duration', label: 'Duration', placeholder: 'e.g. 15:45' },
        { key: 'views', label: 'View count', type: 'number', placeholder: '0' },
        { key: 'channelName', label: 'Channel name', placeholder: 'Channel' },
    ];
    return (
        <>
            <Section label="CONTENT">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div><SubLabel>Block title</SubLabel>
                        <input type="text" value={config.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="e.g. Most Popular" style={inputStyle} /></div>
                    <div><SubLabel>Subtitle</SubLabel>
                        <input type="text" value={config.subtitle} onChange={(e) => onChange({ subtitle: e.target.value })} placeholder="e.g. Most popular this week" style={inputStyle} /></div>
                </div>
            </Section>
            <Section label="CONTENT SOURCE">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div>
                        <SubLabel>Source type</SubLabel>
                        <select value={config.contentSourceType} onChange={(e) => onChange({ contentSourceType: e.target.value })} style={inputStyle}>
                            {['custom', 'trending', 'featured', 'recent'].map((t) => (
                                <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <SubLabel>Item limit</SubLabel>
                        <input type="number" min={1} max={50} value={config.contentLimit} onChange={(e) => onChange({ contentLimit: Number(e.target.value) })} style={inputStyle} />
                    </div>
                </div>
            </Section>
            <Section label="DISPLAY OPTIONS">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <ToggleRow label="Show duration" value={config.showDuration} onChange={(v) => onChange({ showDuration: v })} />
                    <ToggleRow label="Show view count" value={config.showViewCount} onChange={(v) => onChange({ showViewCount: v })} />
                    <ToggleRow label="Show channel name" value={config.showChannelName} onChange={(v) => onChange({ showChannelName: v })} />
                </div>
            </Section>
            <Section label="APPEARANCE">
                <Row>
                    <ColorField label="Background color" value={config.backgroundColor} onChange={(v) => onChange({ backgroundColor: v })} />
                    <ColorField label="Text color" value={config.textColor} onChange={(v) => onChange({ textColor: v })} />
                </Row>
            </Section>
            <Section label="MANUAL CONTENT ITEMS (optional)">
                <p style={{ fontSize: 11, color: '#9ca3af', margin: '0 0 8px' }}>Leave empty to use live source. Add items to override with manual content.</p>
                <ContentItemsEditor items={config.content} onChange={(c) => onChange({ content: c })} fields={fields} addLabel="Add content item" />
            </Section>
        </>
    );
}

function ChannelsFields({ config, onChange }: { config: BlockConfig; onChange: (p: Partial<BlockConfig>) => void }) {
    const fields: ContentItemField[] = [
        { key: 'name', label: 'Channel name', placeholder: 'e.g. Product Updates' },
        { key: 'description', label: 'Description', placeholder: 'Short description' },
        { key: 'thumbnail', label: 'Thumbnail', type: 'image' },
        { key: 'backgroundImage', label: 'Background image', type: 'image' },
        { key: 'videoCount', label: 'Video count', type: 'number', placeholder: '0' },
    ];
    return (
        <>
            <Section label="CONTENT">
                <div style={{ marginBottom: 8 }}>
                    <SubLabel>Block title</SubLabel>
                    <input type="text" value={config.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="e.g. Featured Channels" style={inputStyle} />
                </div>
            </Section>
            <Section label="CHANNEL ITEMS">
                <ContentItemsEditor items={config.content} onChange={(c) => onChange({ content: c })} fields={fields} addLabel="Add channel" />
            </Section>
        </>
    );
}

function TextOverVideoFields({ config, onChange }: { config: BlockConfig; onChange: (p: Partial<BlockConfig>) => void }) {
    const fields: ContentItemField[] = [
        { key: 'title', label: 'Title', placeholder: 'Collection title' },
        { key: 'description', label: 'Description', placeholder: 'Short description' },
        { key: 'thumbnail', label: 'Thumbnail', type: 'image' },
    ];
    return (
        <>
            <Section label="CONTENT">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div><SubLabel>Block title</SubLabel>
                        <input type="text" value={config.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="e.g. Featured Collections" style={inputStyle} /></div>
                    <ToggleRow label="Show description" value={config.showDescription} onChange={(v) => onChange({ showDescription: v })} />
                    <Row>
                        <ColorField label="Background color" value={config.backgroundColor} onChange={(v) => onChange({ backgroundColor: v })} />
                    </Row>
                </div>
            </Section>
            <Section label="CONTENT ITEMS">
                <ContentItemsEditor items={config.content} onChange={(c) => onChange({ content: c })} fields={fields} addLabel="Add item" />
            </Section>
        </>
    );
}

function HeroBannerFields({ config, onChange }: { config: BlockConfig; onChange: (p: Partial<BlockConfig>) => void }) {
    const fields: ContentItemField[] = [
        { key: 'title', label: 'Slide title', placeholder: 'Slide title' },
        { key: 'description', label: 'Description', placeholder: 'Short description' },
        { key: 'thumbnail', label: 'Background image', type: 'image' },
        { key: 'badge', label: 'Badge / tag', placeholder: 'e.g. NEW' },
    ];
    return (
        <>
            <Section label="SETTINGS">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Row>
                        <ColorField label="Background color" value={config.backgroundColor} onChange={(v) => onChange({ backgroundColor: v })} />
                        <ColorField label="Text color" value={config.textColor} onChange={(v) => onChange({ textColor: v })} />
                    </Row>
                    <ToggleRow label="Auto-play slides" value={config.autoPlay} onChange={(v) => onChange({ autoPlay: v })} />
                </div>
            </Section>
            <Section label="SLIDES">
                <ContentItemsEditor items={config.content} onChange={(c) => onChange({ content: c })} fields={fields} addLabel="Add slide" />
            </Section>
        </>
    );
}

function GenericFields({ config, onChange }: { config: BlockConfig; onChange: (p: Partial<BlockConfig>) => void }) {
    return (
        <>
            <Section label="CONTENT">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div><SubLabel>Title</SubLabel>
                        <input type="text" value={config.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="Block title" style={inputStyle} /></div>
                    <div><SubLabel>Subtitle</SubLabel>
                        <input type="text" value={config.subtitle} onChange={(e) => onChange({ subtitle: e.target.value })} placeholder="Optional subtitle" style={inputStyle} /></div>
                </div>
            </Section>
            <Section label="CONTENT SOURCE">
                <Row>
                    <Col>
                        <SubLabel>Source type</SubLabel>
                        <select value={config.contentSourceType} onChange={(e) => onChange({ contentSourceType: e.target.value })} style={inputStyle}>
                            {['custom', 'trending', 'featured', 'recent', 'channel', 'category'].map((t) => (
                                <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                            ))}
                        </select>
                    </Col>
                    <Col>
                        <SubLabel>Limit</SubLabel>
                        <input type="number" min={1} max={50} value={config.contentLimit} onChange={(e) => onChange({ contentLimit: Number(e.target.value) })} style={inputStyle} />
                    </Col>
                </Row>
            </Section>
            <Section label="APPEARANCE">
                <Row>
                    <ColorField label="Background" value={config.backgroundColor} onChange={(v) => onChange({ backgroundColor: v })} />
                    <ColorField label="Text" value={config.textColor} onChange={(v) => onChange({ textColor: v })} />
                </Row>
            </Section>
            <Section label="CTA BUTTON (optional)">
                <Row>
                    <Col><SubLabel>Label</SubLabel>
                        <input type="text" value={config.ctaButtonLabel} onChange={(e) => onChange({ ctaButtonLabel: e.target.value })} placeholder="e.g. View all" style={inputStyle} /></Col>
                    <Col><SubLabel>URL</SubLabel>
                        <input type="text" value={config.ctaButtonUrl} onChange={(e) => onChange({ ctaButtonUrl: e.target.value })} placeholder="/path" style={inputStyle} /></Col>
                </Row>
            </Section>
        </>
    );
}


// TYPE → FIELDS ROUTER  

export function TypeSpecificFields({ def, config, onChange }: {
    def: BlockDefinition;
    config: BlockConfig;
    onChange: (p: Partial<BlockConfig>) => void;
}) {
    switch (def.type) {
        case 'welcome-banner': return <WelcomeBannerFields config={config} onChange={onChange} />;
        case 'search-filter': return <SearchFilterFields config={config} onChange={onChange} />;
        case 'quick-links': return <QuickLinksFields config={config} onChange={onChange} />;
        case 'highlighted-categories': return <CategoriesFields config={config} onChange={onChange} isTiled />;
        case 'categories-list': return <CategoriesFields config={config} onChange={onChange} />;
        case 'medium-carousel':
        case 'horizontal-scroll':
        case 'vertical-list':
        case 'thumbnail-grid': return <CarouselFields config={config} onChange={onChange} />;
        case 'all-channels':
        case 'featured-channel': return <ChannelsFields config={config} onChange={onChange} />;
        case 'text-over-video': return <TextOverVideoFields config={config} onChange={onChange} />;
        case 'hero-carousel': return <HeroBannerFields config={config} onChange={onChange} />;
        case 'custom-promo':
            return (
                <Section label="HTML CONTENT">
                    <p style={{ fontSize: 11, color: '#9ca3af', margin: '0 0 8px' }}>Paste raw HTML, CSS, and JS. Tags will not be escaped.</p>
                    <HtmlEditor value={config.htmlContent} onChange={(html) => onChange({ htmlContent: html })} />
                </Section>
            );
        case 'header': return <HeaderFields config={config} onChange={onChange} />;
        case 'footer': return <FooterFields config={config} onChange={onChange} />;
        default: return <GenericFields config={config} onChange={onChange} />;
    }
}


// SHARED CONFIG PANEL 

export function SharedConfigPanel({ def, config, onChange, onSubmit, submitLabel, isSubmitting }: {
    def: BlockDefinition;
    config: BlockConfig;
    onChange: (p: Partial<BlockConfig>) => void;
    onSubmit: () => void;
    submitLabel: string;
    isSubmitting: boolean;
}) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 0' }}>

                {/* Container width */}
                <Section label="CONTAINER">
                    <div style={{ display: 'flex', gap: 8 }}>
                        {[{ v: 'full', label: 'Full width', icon: '⬜' }, { v: 'contained', label: 'Contained', icon: '⊡' }].map((opt) => {
                            const active = config.fullWidth === (opt.v === 'full');
                            return (
                                <button key={opt.v} onClick={() => onChange({ fullWidth: opt.v === 'full' })}
                                    style={{ flex: 1, padding: '9px 8px', borderRadius: 8, border: `1.5px solid ${active ? '#3b82f6' : '#e5e7eb'}`, background: active ? '#eff6ff' : 'white', color: active ? '#1d4ed8' : '#6b7280', fontWeight: 500, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span>{opt.icon}</span>{opt.label}
                                </button>
                            );
                        })}
                    </div>
                </Section>

                {/* Layout style chips */}
                {def.supportedLayouts.length > 1 && (
                    <Section label="DISPLAY STYLE">
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {def.supportedLayouts.map((style) => (
                                <button key={style} onClick={() => onChange({ layoutStyle: style })}
                                    style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${config.layoutStyle === style ? '#3b82f6' : '#e5e7eb'}`, background: config.layoutStyle === style ? '#eff6ff' : '#f9fafb', color: config.layoutStyle === style ? '#1d4ed8' : '#6b7280', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                                    {LAYOUT_STYLE_LABELS[style]}
                                </button>
                            ))}
                        </div>
                    </Section>
                )}

                {/* Type-specific fields */}
                <TypeSpecificFields def={def} config={config} onChange={onChange} />

                <div style={{ height: 24 }} />
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 20px', borderTop: '1px solid #f3f4f6', background: 'white', flexShrink: 0 }}>
                <button onClick={onSubmit} disabled={isSubmitting}
                    style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: isSubmitting ? '#93c5fd' : '#2563eb', color: 'white', fontWeight: 600, fontSize: 14, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                    {isSubmitting ? 'Saving…' : submitLabel}
                </button>
            </div>
        </div>
    );
}